import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Socket, io } from "socket.io-client";

import { SOCKET_URL, getClientDomain } from "@/configs";
import { closeBetSlip, dispatch, setChatRoom, useSelector } from "@/store";
import { ChatLang, Message } from "@/types";

import { useToken } from "./useToken";

const MAX_MESSAGE_LENGTH = 200;
const HISTORY_PAGE_SIZE = 30;
const SWITCH_ROOM_TIMEOUT_MS = 8000;

export const CHAT_LANGS: ChatLang[] = ["en", "es", "it", "pt"];

const toChatLang = (value?: string): ChatLang => {
  const code = (value ?? "").slice(0, 2).toLowerCase();
  return (CHAT_LANGS as string[]).includes(code) ? (code as ChatLang) : "en";
};

type ChatState = {
  messages: Message[];
  messageInput: string;
  isConnected: boolean;
  onlineCount: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMoreHistory: boolean;
  connectionError: string | null;
  roomLang: ChatLang;
};

const useChatRoomGame = () => {
  const token = useToken();
  const { chatRoomOpen: open } = useSelector((state) => state.auth);
  const { i18n } = useTranslation();
  const siteLang = toChatLang(i18n.resolvedLanguage ?? i18n.language);

  const [state, setState] = useState<ChatState>({
    messages: [],
    messageInput: "",
    isConnected: false,
    onlineCount: 0,
    isLoading: true,
    isLoadingMore: false,
    hasMoreHistory: true,
    connectionError: null,
    roomLang: siteLang,
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const prevMessagesLengthRef = useRef<number>(0);
  const loadingMoreRef = useRef<boolean>(false);
  const skipNextAutoScrollRef = useRef<boolean>(false);
  // Read by the handshake, so a reconnect rejoins the room the player is in.
  const langRef = useRef<ChatLang>(siteLang);
  // Once the player picks a room, the site language no longer moves them.
  const langPickedRef = useRef<boolean>(false);

  const isSendDisabled = useMemo(
    () =>
      !state.messageInput.trim() ||
      state.messageInput.length > MAX_MESSAGE_LENGTH ||
      !state.isConnected,
    [state.messageInput, state.isConnected],
  );

  const scrollToBottom = useCallback(() => {
    if (skipNextAutoScrollRef.current) {
      skipNextAutoScrollRef.current = false;
      prevMessagesLengthRef.current = state.messages.length;
      return;
    }
    if (state.messages.length > prevMessagesLengthRef.current || open) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      prevMessagesLengthRef.current = state.messages.length;
    }
  }, [state.messages.length, open]);

  const handleSendMessage = useCallback(() => {
    if (!isSendDisabled && socketRef.current) {
      const messageToSend = state.messageInput.trim();
      socketRef.current.emit(
        "sendMessage",
        { message: messageToSend },
        (res?: { ok: boolean; error?: string }) => {
          if (res && !res.ok && res.error) toast.error(res.error, { hideProgressBar: true });
        },
      );
      setState((prev) => ({ ...prev, messageInput: "" }));
    }
  }, [state.messageInput, isSendDisabled]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey && !isSendDisabled) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage, isSendDisabled],
  );

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length <= MAX_MESSAGE_LENGTH) {
      setState((prev) => ({ ...prev, messageInput: e.target.value }));
    }
  }, []);

  /** Sets the draft directly: the emoji picker inserts at the cursor. */
  const setMessageInput = useCallback((value: string) => {
    setState((prev) => ({ ...prev, messageInput: value.slice(0, MAX_MESSAGE_LENGTH) }));
  }, []);

  const joinRoom = useCallback((lang: ChatLang) => {
    const previous = langRef.current;
    if (lang === previous) return;
    langRef.current = lang;
    setState((prev) => ({ ...prev, roomLang: lang }));

    const socket = socketRef.current;
    if (!socket?.connected) return;
    // The server answers with the new room's history, which replaces the list;
    // the old list stays underneath so a refusal can fall back to it.
    setState((prev) => ({ ...prev, isLoading: true }));
    socket
      .timeout(SWITCH_ROOM_TIMEOUT_MS)
      .emit(
        "switchRoom",
        { lang },
        (err: Error | null, res?: { ok: boolean; lang?: string; error?: string }) => {
          if (!err && res?.ok) return;
          langRef.current = previous;
          setState((prev) => ({ ...prev, roomLang: previous, isLoading: false }));
          toast.error(res?.error || "Could not switch room", { hideProgressBar: true });
        },
      );
  }, []);

  const switchRoom = useCallback(
    (lang: ChatLang) => {
      langPickedRef.current = true;
      joinRoom(lang);
    },
    [joinRoom],
  );

  useEffect(() => {
    if (!langPickedRef.current) joinRoom(siteLang);
  }, [siteLang, joinRoom]);

  const toggle = () => {
    dispatch(setChatRoom(!open));
    if (!open) {
      dispatch(closeBetSlip());
    }
  };

  const loadMoreMessages = useCallback(() => {
    const socket = socketRef.current;
    if (!socket || loadingMoreRef.current || !state.hasMoreHistory || state.messages.length === 0) {
      return;
    }

    loadingMoreRef.current = true;
    setState((prev) => ({ ...prev, isLoadingMore: true }));

    const oldest = state.messages[0]?.createdAt;

    socket.emit(
      "loadMoreMessages",
      { before: oldest },
      (res: { messages: Message[]; hasMore: boolean } | undefined) => {
        skipNextAutoScrollRef.current = true;
        setState((prev) => ({
          ...prev,
          messages: [...(res?.messages ?? []), ...prev.messages],
          hasMoreHistory: res?.hasMore ?? false,
          isLoadingMore: false,
        }));
        loadingMoreRef.current = false;
      },
    );
  }, [state.hasMoreHistory, state.messages]);

  useEffect(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  useEffect(() => {
    setState((prev) => ({ ...prev, isLoading: true, connectionError: null }));

    const socket = io(`${SOCKET_URL}/chat-room`, {
      transports: ["websocket"],
      // Keep retrying with backoff; a dropped connection should come back on its own.
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      auth: (cb) =>
        cb({ ...(token ? { token } : {}), domain: getClientDomain(), lang: langRef.current }),
    });
    socketRef.current = socket;

    const socketHandlers = {
      connect: () => setState((prev) => ({ ...prev, isConnected: true, connectionError: null })),
      disconnect: () => setState((prev) => ({ ...prev, isConnected: false })),
      messageHistory: (history: Message[]) =>
        setState((prev) => ({
          ...prev,
          messages: history,
          isLoading: false,
          hasMoreHistory: history.length >= HISTORY_PAGE_SIZE,
        })),
      newMessage: (message: Message) =>
        setState((prev) => ({
          ...prev,
          messages: [...prev.messages, message],
        })),
      userCount: (count: number) => setState((prev) => ({ ...prev, onlineCount: count })),
      // Only an empty chat shows the error in place of the list; otherwise the history stays.
      connect_error: () =>
        setState((prev) => ({
          ...prev,
          isConnected: false,
          connectionError: prev.messages.length ? null : "Failed to connect to chat server",
          isLoading: false,
        })),
      error: (err: { message?: string }) =>
        toast.error(err?.message || "Chat error occurred", { hideProgressBar: true }),
    };

    Object.entries(socketHandlers).forEach(([event, handler]) => {
      socket.on(event, handler);
    });

    return () => {
      Object.keys(socketHandlers).forEach((event) => {
        socket.off(event);
      });
      socket.disconnect();
    };
  }, [token]);

  return {
    open,
    state,
    messagesEndRef,
    socketRef,
    prevMessagesLengthRef,
    isSendDisabled,
    scrollToBottom,
    handleSendMessage,
    handleKeyPress,
    handleInputChange,
    setMessageInput,
    loadMoreMessages,
    switchRoom,
    toggle,
  };
};

const ChatRoomGameContext = createContext<ReturnType<typeof useChatRoomGame> | null>(null);

export const ChatRoomGameProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const value = useChatRoomGame();

  return <ChatRoomGameContext.Provider value={value}>{children}</ChatRoomGameContext.Provider>;
};

export const useChatRoomGameContext = () => {
  const context = useContext(ChatRoomGameContext);
  if (!context)
    throw new Error("useChatRoomGameContext must be used within a ChatRoomGameProvider");

  return context;
};
